import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, of, shareReplay, switchMap } from 'rxjs';
import { CampaignService } from './campaign.service';
import { ClientSessionService } from '../../cliente/services/cliente-session.service';
import { Option } from '../components/campana-select/campana-select-component';
import { CampaignClient } from '../models/campaign-client.interface';
import { AppIcons } from '@/assets/icons/icons';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AnalysisService } from '../../reportes/services/analysis.service';
import { UserRoleStore } from '@/app/core/states/user-role-stare/client-role-state.store';
import { ClientStore } from '@/app/core/states/clientes-state/client-state.store';
import { toObservable } from '@angular/core/rxjs-interop';
import { ClientsService } from '../../clientes/services/clients.service';

type Color = 'success' | 'warning' | 'danger' | 'gray' | '';
export interface CardResumen {
  titulo: string;
  subtitulo: string;
  icon: SafeHtml | string;
  colorIcon: Color;
}
export interface CardPorcentaje {
  titulo: string;
  subtitulo: string;
  imagen: SafeHtml | string;
  colorIcon: Color;
  percentage: string;
}

@Injectable({ providedIn: 'root' })
export class CampaignStoreService {

  private campaignService = inject(CampaignService);
  private clientSessionService = inject(ClientSessionService);
  private readonly sanitizer = inject(DomSanitizer);
  private analysis = inject(AnalysisService);
  private userRoleStore = inject(UserRoleStore);
  private clientStore = inject(ClientStore);
  private clientsService = inject(ClientsService);

  private readonly guideId = 'guia-i';

  // Opciones para el dropdown
  private campaignsOptionsSubject = new BehaviorSubject<Option[]>([]);
  campaignsOptions$ = this.campaignsOptionsSubject.asObservable();

  // Lista completa para lógica de negocio (cards, tablas, etc.)
  private campaignsRawSubject = new BehaviorSubject<CampaignClient[]>([]);
  campaignsRaw$ = this.campaignsRawSubject.asObservable();

  // Selección actual (opción del dropdown)
  private selectedCampaignSubject = new BehaviorSubject<Option | null>(null);
  selectedCampaign$ = this.selectedCampaignSubject.asObservable();

  /** Carga campañas para el cliente en sesión */
  loadCampaigns(clientIdParam: string): void {
    if (this.campaignsOptionsSubject.value.length) return;

    const user = this.userRoleStore.user();
    let clientId = user?.attributes?.['publicId'] || clientIdParam;

    if (!clientId) {
      console.error('No hay cliente en sesión');
      return;
    }

    this.campaignService.getCampaignsByClient(clientId).subscribe({
      next: (list: CampaignClient[]) => {
        const sortedList = [...list].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
        this.campaignsRawSubject.next(sortedList);

        const options: Option[] = sortedList.map(c => ({
          value: c.publicId,
          label: c.name
        }));
        this.campaignsOptionsSubject.next(options);

        // NO auto-seleccionar ninguna campaña - dejar que el usuario elija
        // Removido: if (!this.selectedCampaignSubject.value && options.length) {
        // Removido:   this.setSelectedCampaign(options[0]);
        // Removido: }
      },
      error: (err) => console.error('Error cargando campañas', err)
    });
  }

  /** Carga campañas para un cliente específico (usado por admin) */
  loadCampaignsForClient(clientId: string): void {
    if (!clientId) {
      this.clearCampaigns();
      this.clientStore.resetData();
      return;
    }

    this.clientsService.getClient(clientId).subscribe(client => {
      if (client) {
        this.clientStore.setClient(client);
      }
    });

    this.campaignService.getCampaignsByClient(clientId).subscribe({
      next: (list: CampaignClient[]) => {
        const sortedList = [...list].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
        this.campaignsRawSubject.next(sortedList);

        const options: Option[] = sortedList.map(c => ({
          value: c.publicId,
          label: c.name
        }));
        this.campaignsOptionsSubject.next(options);

        const currentSelection = this.selectedCampaignSubject.value;
        const currentSelectionIsValid = currentSelection && options.some(o => o.value === currentSelection.value);

        if (!currentSelectionIsValid) {
          this.setSelectedCampaign(null);
        }
      },
      error: (err) => {
        console.error('Error cargando campañas', err);
        this.clearCampaigns();
      }
    });
  }

  /** Limpia los datos de campañas del store */
  clearCampaigns(): void {
    this.campaignsRawSubject.next([]);
    this.campaignsOptionsSubject.next([]);
    this.selectedCampaignSubject.next(null);
  }

  /** Actualiza la campaña seleccionada (refleja en todas las vistas) */
  setSelectedCampaign(option: Option | null): void {
    this.selectedCampaignSubject.next(option);
  }

  /** Campaña completa (CampaignClient) derivada del Option seleccionado */
  readonly selectedCampaignClient$ = combineLatest([
    this.selectedCampaign$,
    this.campaignsRaw$
  ]).pipe(
    map(([opt, list]) => {
      if (!opt || !list) return null;
      return list.find(c => c.publicId === opt.value) ?? null;
    }),
    shareReplay(1)
  );

  /** Una sola llamada a la API */
  private readonly finalAssessments$ = this.selectedCampaignClient$.pipe(
    switchMap(camp => {
      if (!camp) {
        return of([] as Array<{ finalAssessment?: string; rating?: string; totalRespondents: number }>);
      }
      return this.analysis.getFinalAssessments({
        campaign: camp.publicId,
        guideId: this.guideId,
      });
    }),
    shareReplay(1)
  );

  /** Número real de cuestionarios respondidos (suma de final-assessments) */
  readonly respondedCount$ = this.finalAssessments$.pipe(
    map(rows => rows.reduce((acc, r) => acc + (r?.totalRespondents ?? 0), 0)),
    shareReplay(1)
  );

  /** Número de personas con ATS ontenida de final-assesments */
  readonly conAtsCount$ = this.finalAssessments$.pipe(
    map(rows =>
      rows
        .filter(r => (r.finalAssessment ?? r.rating) !== 'Nulo o despreciable')
        .reduce((acc, r) => acc + (r?.totalRespondents ?? 0), 0)
    ),
    shareReplay(1)
  );


  /** Helper para obtener el objeto raw de una campaña */
  getCampaignByPublicId(publicId: string): CampaignClient | null {
    return this.campaignsRawSubject.value.find(c => c.publicId === publicId) ?? null;
  }

  /** Cards chicas (las 3 de la izquierda) con datos reales */
  readonly summaryCards$ = combineLatest([
    this.selectedCampaignClient$,
    this.respondedCount$
  ]).pipe(
    map(([camp, responded]) => {
      if (!camp) return [[], []] as CardResumen[][];

      const new_Date: Date = new Date();
      const fechaString : string = new_Date.toISOString();
      const empleadosRegistrados = camp.totalEmployees;
      const porcentaje = empleadosRegistrados > 0
        ? Math.round((responded / empleadosRegistrados) * 100)
        : 0;
      return [
        [
          {
            titulo: camp.endDate < fechaString ? 'Desactivada' : 'Activada',
            subtitulo: 'Campaña',
            icon: this.sanitizer.bypassSecurityTrustHtml(AppIcons.CIRCLE),
            colorIcon: camp.endDate < fechaString ? 'danger' : 'success',
          },
          {
            titulo: String(empleadosRegistrados),
            subtitulo: 'Empleados registrados',
            icon: this.sanitizer.bypassSecurityTrustHtml(AppIcons.USER),
            colorIcon: 'gray',
          },
        ],
        [
          {
            titulo: `${responded}/${empleadosRegistrados}`,
            subtitulo: 'Cuestionarios respondidos',
            icon: this.sanitizer.bypassSecurityTrustHtml(AppIcons.CIRCLE),
            colorIcon: this.getColorByCumplimiento(porcentaje),
          },
          {
            titulo: `${this.getDiasRestantes(camp.endDate)}`,
            subtitulo: 'Días para finalizar',
            icon: this.sanitizer.bypassSecurityTrustHtml(AppIcons.FLECHA),
            colorIcon: 'success',
          },
        ],
      ];
    }),
    shareReplay(1)
  );


  /** Card de porcentaje (semicírculo) con porcentaje real */
  readonly percentageCard$ = combineLatest([
    this.selectedCampaignClient$,
    this.respondedCount$
  ]).pipe(
    map(([camp, responded]) => {
      if (!camp) return null;

      const empleadosRegistrados = camp.totalEmployees;
      const porcentaje = empleadosRegistrados > 0
        ? Math.round((responded / empleadosRegistrados) * 100)
        : 0;

      return {
        titulo: 'Nivel de cumplimiento',
        subtitulo: '(mín. 80% de acuerdo a empleados y cuestionarios respondidos)',
        imagen: this.sanitizer.bypassSecurityTrustHtml(AppIcons.SEMI_CIRCLE),
        colorIcon: this.getColorByCumplimiento(porcentaje),
        percentage: String(porcentaje),
      } as CardPorcentaje;
    }),
    shareReplay(1)
  );


  /** Total de empleados de la campaña seleccionada */
  readonly totalEmployees$ = this.selectedCampaignClient$.pipe(
    map(camp => camp?.totalEmployees ?? 0),
    distinctUntilChanged()
  );

  private getDiasRestantes(fechaFin: string): number {
    const fin = new Date(fechaFin);
    const hoy = new Date();
    const diff = fin.getTime() - hoy.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  private getColorByCumplimiento(porcentaje: number): Color {
    if (porcentaje <= 40) return 'danger';
    if (porcentaje <= 80) return 'warning';
    return 'success';
  }

  /** Fechas formateadas y duración en días  */
  readonly campaignMeta$ = this.selectedCampaignClient$.pipe(
    map(camp => {
      if (!camp) {
        return {
          startDateLabel: '—',
          endDateLabel: '—',
          durationDays: '—' as number | string,
        };
      }

      const start = camp.startDate ? new Date(camp.startDate) : null;
      const end   = camp.endDate   ? new Date(camp.endDate)   : null;

      const fmt = (d: Date | null) =>
        d ? new Intl.DateTimeFormat('es-MX', {
            day: '2-digit', month: 'long', year: 'numeric'
          }).format(d)
          : '—';

      const durationDays =
        start && end
          ? Math.ceil(Math.max(0, end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
          : '—';

      return {
        startDateLabel: fmt(start),
        endDateLabel: fmt(end),
        durationDays,
      };
    }),
    shareReplay(1)
  );

  /** Nombre fiscal del cliente para la card */
  readonly companyName$ = toObservable(this.clientStore.client).pipe(
    map(client => client?.businessName || client?.name || '—'),
    shareReplay(1)
  );

  /** Cliente completo para acceder a otros datos como contactName */
  readonly client$ = toObservable(this.clientStore.client);
}
